import { Box, MenuItem, Select, Tooltip, Typography } from "@mui/material";
import TextView from "../../../TextView";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ReactComponent as FontColor } from "@assets/images/FontColor.svg";
import { ReactComponent as Reset } from "@assets/images/Reset.svg";
import { useState, useEffect, useRef } from "react";
import * as Styled from "./styles.js";
import CustomColorPicker from "../../../CustomColorPicker/index.jsx";
import ColorPopup from "../../Shared/ColorPopup/index.jsx";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";

const StyleInfoCard = ({
  title,
  background = "#F6F7F9",
  applyButton = true,
  handleApplyChanges = () => {},
  fonts = [
    { label: "DM Sans", fontFamily: "'DM Sans', sans-serif" },
    { label: "Arial", fontFamily: "Arial, Helvetica, sans-serif" },
    { label: "Georgia", fontFamily: "Georgia, serif" },
    { label: "Impact", fontFamily: "Impact, Charcoal, sans-serif" },
    { label: "Tahoma", fontFamily: "Tahoma, Geneva, sans-serif" },
    { label: "Times New Roman", fontFamily: "'Times New Roman', Times, serif" },
    { label: "Verdana", fontFamily: "Verdana, Geneva, sans-serif" },
    { label: "Poppins", fontFamily: "'Poppins', sans-serif" },
    { label: "Quando", fontFamily: "'Quando', serif" },
  ],
  inputValue,
  inputLable = "px",
  handleInputValueChange,
  minInputValue = 0,
  maxInputValue = 96,
  selectedFont = "Poppins",
  handleFontChange,
  selectedColor = "#000000",
  onConfirmCustomColor = () => {},
  borderBottom = false,
  tooltip = null,
  handleResetFontColor = null,
  type,
}) => {
  const { getPaletteColor } = usePaletteColor();
  const [colorPickerVisible, setColorPickerVisible] = useState(false);
  const [temporaryColor, setTemporaryColor] = useState(
    selectedColor || "#FFFFFF"
  );
  const [showColorPopup, setShowColorPopup] = useState(false);
  const colorPopupRef = useRef(null);

  const handleFontColor = () => {
    setShowColorPopup(!showColorPopup);
  };

  useEffect(() => {
    if (showColorPopup && colorPopupRef.current) {
      // Add a slight delay to ensure the element is rendered
      setTimeout(() => {
        const colorPaletteElement =
          colorPopupRef.current?.querySelector(".MuiGrid-container");
        if (colorPaletteElement) {
          colorPaletteElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }, 100);
    }
  }, [showColorPopup]);

  const handleTemporaryColorChange = (color) => {
    setTemporaryColor(color);
  };

  const handleValueChange = (e) => {
    const { value } = e.target;
    if (value.trim() === "") {
      handleInputValueChange("");
      return;
    }
    const parsedValue = parseInt(value, 10);
    if (
      parsedValue >= minInputValue &&
      parsedValue <= maxInputValue &&
      Number.isInteger(parsedValue)
    ) {
      handleInputValueChange(parsedValue);
    }
  };

  const handleConfirmColor = (color) => {
    onConfirmCustomColor(color);
    setColorPickerVisible(false);
  };

  const selectedFontObj = fonts.find((font) => font.label === selectedFont);

  return (
    <Box sx={{ maxWidth: "100%" }}>
      <Box
        sx={{
          padding: "16px",
          borderRadius: "8px",
          backgroundColor: background,
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          borderBottom: borderBottom && "1px solid #F7F2FA",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <TextView
            variant="subtitle"
            style={{
              color: "#123051",
            }}
          >
            {title}
          </TextView>
          <Tooltip title={tooltip} placement="top" arrow>
            <InfoOutlinedIcon
              sx={{
                color: "#123051",
                cursor: "pointer",
              }}
            />
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: handleResetFontColor ? "8px" : "16px",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Tooltip
              title={`Default ${type} Font Color: ${selectedColor}`}
              placement="top"
              arrow
            >
              <Box
                onClick={handleFontColor}
                sx={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  backgroundColor: selectedColor,
                  cursor: "pointer",
                  border: "1px solid #98A2B3",
                }}
              />
            </Tooltip>

            {handleResetFontColor && (
              <Tooltip
                title={`Restore text colors that were selectively customized in the editor to the default ${type} font color.`}
                placement="top"
                arrow
              >
                <Reset
                  style={{ width: "24px", height: "24px", cursor: "pointer" }}
                  onClick={handleResetFontColor}
                />
              </Tooltip>
            )}
          </Box>

          <Select
            labelId="font-select-label"
            id="font-select"
            value={selectedFont}
            onChange={(event) => handleFontChange(event.target.value)}
            sx={{
              minWidth: "60%",
              height: "40px",
              borderRadius: "8px",
              backgroundColor: "white",
              border: "1px solid #EAECF0",
              color: "#565556",
              fontFamily: selectedFontObj
                ? selectedFontObj.fontFamily
                : "'DM Sans', sans-serif",
              fontSize: "14px",
              fontWeight: "400",

              ".MuiSvgIcon-root": {
                color: "#98A2B3",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: "8px",
                  backgroundColor: "white",
                  border: "1px solid #EAECF0",
                  "& .MuiMenuItem-root": {
                    color: "#565556",
                  },
                },
              },
            }}
            renderValue={(selected) => {
              const font = fonts.find((f) => f.label === selected);
              return (
                <span
                  style={{
                    fontFamily: font
                      ? font.fontFamily
                      : "'DM Sans', sans-serif",
                  }}
                >
                  {selected}
                </span>
              );
            }}
          >
            {fonts.map((font) => (
              <MenuItem
                key={font.label}
                value={font.label}
                sx={{ fontFamily: font.fontFamily }}
              >
                {font.label}
              </MenuItem>
            ))}
          </Select>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Styled.StyledInput
              value={inputValue}
              disableUnderline
              placeholder=""
              onChange={handleValueChange}
            />
            <Styled.PxText>{inputLable}</Styled.PxText>
          </Box>
        </Box>
      </Box>

      {applyButton && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            marginTop: "8px",
          }}
        >
          <Typography
            sx={{
              cursor: "pointer",
              color: "#9B5AF8",
              transition: "all 0.2s ease-in-out",
              fontSize: "16px",
              fontWeight: "500",
              fontFamily: "DM Sans",

              "&:hover": {
                scale: "1.05",
                transition: "all 0.2s ease-in-out",
              },
            }}
            onClick={handleApplyChanges}
          >
            Apply
          </Typography>
        </Box>
      )}

      {showColorPopup && (
        <div ref={colorPopupRef}>
          <ColorPopup
            allowTransparent={false}
            background={selectedColor}
            handleChangeBackgroundColor={(color) => {
              const actualColor = getPaletteColor(color);
              onConfirmCustomColor(actualColor);
            }}
            resetBackgroundColor={() => {
              if (handleResetFontColor) handleResetFontColor();
              setShowColorPopup(false);
            }}
            showSlider={false}
            isHideGradient={true}
            showAccordion={false}
          />
        </div>
      )}

      <CustomColorPicker
        customStyle={{
          position: "fixed",
          left: "70.5vw",
          bottom: "14vh",
        }}
        visible={colorPickerVisible}
        onClose={() => setColorPickerVisible(false)}
        onConfirm={handleConfirmColor}
        color={temporaryColor}
        onColorChange={handleTemporaryColorChange}
      />
    </Box>
  );
};

export default StyleInfoCard;
